"use client";

import { Spinner } from "@/components/shadcn/spinner";
import TurnstileWidget, {
    TurnstileWidgetHandle,
} from "@/components/turnstile-widget";
import { authClient } from "@/lib/auth/client";
import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import { ChevronRight } from "lucide-react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "../shadcn/button";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "../shadcn/form";
import { Input } from "../shadcn/input";

const ForgetForm = ({ showIntro = true }: { showIntro?: boolean }) => {
    const t = useTranslations("auth");
    const formSchema = z.object({
        email: z
            .string({ required_error: t("emailRequired") })
            .email(t("emailInvalid")),
    });
    type InputType = z.infer<typeof formSchema>;
    const [isLoading, setIsLoading] = useState(false);
    const [captchaToken, setCaptchaToken] = useState("");
    const captchaRef = useRef<TurnstileWidgetHandle>(null);

    const form = useForm<InputType>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            email: "",
        },
    });

    async function onSubmit(values: InputType) {
        if (!captchaToken) {
            toast.error(t("loginFailed"), {
                description: t("turnstilePending"),
            });
            return;
        }

        try {
            setIsLoading(true);

            const captchaResponse = await axios.post("/api/turnstile-verify", {
                token: captchaToken,
            });

            if (captchaResponse?.data?.success === false) {
                toast.error(t("loginFailed"), {
                    description: t("turnstileFailed"),
                });
                return;
            }

            const { error } = await authClient.requestPasswordReset({
                email: values.email,
                redirectTo: "/reset-password",
            });
            if (error) {
                toast.error(t("somethingWrong"), {
                    description: error.message ?? t("genericRetry"),
                });
            } else {
                toast.success(t("forgotSuccessTitle"), {
                    description: t("forgotSuccessDesc"),
                });
            }
        } catch (error) {
            console.error(error);
            toast.error(t("somethingWrong"), {
                description: t("genericRetry"),
            });
        } finally {
            captchaRef.current?.reset();
            setIsLoading(false);
        }
    }

    return (
        <>
            {showIntro && (
                <>
                    <Image
                        src="/images/forgot-password.png"
                        alt={t("forgotImageAlt")}
                        className="mx-auto my-4 h-auto max-w-full"
                        width={216}
                        height={143}
                    />
                    <p className="mb-4 text-center text-2xl font-medium">
                        {t("forgotTitle")}
                    </p>
                    <hr className="mb-4 border-neutral-500" />
                    <p className="mb-8 text-center">{t("forgotIntro")}</p>
                </>
            )}

            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <div className="flex flex-col gap-5 sm:gap-6">
                        <FormField
                            control={form.control}
                            name="email"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>E-posta Adresi</FormLabel>
                                    <FormControl>
                                        <Input
                                            type="text"
                                            placeholder="E-posta adresinizi girin"
                                            className={`${
                                                form.formState.errors.email &&
                                                "border-danger"
                                            }`}
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <TurnstileWidget
                            ref={captchaRef}
                            onToken={setCaptchaToken}
                            action="inquirySubmit"
                        />

                        <div className="flex items-center justify-between">
                            <Button
                                className="flex w-full justify-between pr-4 pl-10"
                                disabled={isLoading || !captchaToken}
                            >
                                <span>{t("send")}</span>{" "}
                                {isLoading ? (
                                    <Spinner data-icon="inline-end" />
                                ) : (
                                    <ChevronRight data-icon="inline-end" />
                                )}
                            </Button>
                        </div>
                    </div>
                </form>
            </Form>
        </>
    );
};

export default ForgetForm;
